import { useState, useMemo } from "react";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Shield, ShieldCheck } from "lucide-react";
import CaprocureLogo from "@/components/CaprocureLogo";
import { toast } from "sonner";
import PasswordChecklist from "@/components/PasswordChecklist";
import { validatePasswordClient } from "@/lib/password";

// Consume a one-time token (from the ?token=… query param) and set a new
// password. Successful confirm invalidates every existing session for this
// user via the backend's token_version bump, so the redirect back to /login
// is expected — no auto-login here.
export default function ResetPassword() {
  const [params] = useSearchParams();
  const token = useMemo(() => (params.get("token") || "").trim(), [params]);
  const [pw, setPw] = useState("");
  const [confirm, setConfirm] = useState("");
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");
  const [done, setDone] = useState(false);
  const nav = useNavigate();

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    if (!token) { setErr("Missing token — reopen the link from your email."); return; }
    // M-5: mirror backend policy client-side.
    const pwErr = validatePasswordClient(pw);
    if (pwErr) { setErr(pwErr); return; }
    if (pw !== confirm) { setErr("Passwords do not match."); return; }
    setLoading(true);
    try {
      await api.post("/auth/password-reset/confirm", { token, new_password: pw });
      toast.success("Password updated — please sign in");
      setDone(true);
      setTimeout(() => nav("/login"), 1500);
    } catch (e) {
      setErr(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center p-6">
      <Card className="max-w-lg w-full p-10 border-slate-200">
        <Link to="/" className="flex items-center gap-3 mb-6">
          <div className="w-8 h-8 rounded-md bg-[#0A2540] flex items-center justify-center">
            <Shield className="w-4 h-4 text-white" />
          </div>
          <div className="font-display font-extrabold text-[#0A2540]">Caprocure</div>
        </Link>

        {done ? (
          <div className="text-center py-6" data-testid="reset-success">
            <div className="w-12 h-12 rounded-full bg-emerald-50 border border-emerald-200 flex items-center justify-center mx-auto">
              <ShieldCheck className="w-6 h-6 text-emerald-600" />
            </div>
            <div className="eyebrow mt-4">All set</div>
            <h2 className="font-display text-2xl font-bold text-[#0A2540] mt-1">Password updated</h2>
            <p className="text-sm text-slate-500 mt-2">Redirecting you to sign in…</p>
          </div>
        ) : (
          <>
            <div className="eyebrow mb-2">Password reset</div>
            <h2 className="font-display text-2xl font-bold text-[#0A2540]">Choose a new password</h2>
            <p className="text-sm text-slate-500 mt-2">
              This link is single-use and expires within an hour of being sent. Any active sessions will be signed out after you reset.
            </p>
            <form onSubmit={submit} className="mt-6 space-y-4">
              <div>
                <Label>New password *</Label>
                <Input type="password" value={pw} onChange={(e) => setPw(e.target.value)} required minLength={10} data-testid="reset-pw-input" />
                <PasswordChecklist password={pw} />
              </div>
              <div>
                <Label>Confirm new password *</Label>
                <Input type="password" value={confirm} onChange={(e) => setConfirm(e.target.value)} required minLength={10} data-testid="reset-confirm-input" />
              </div>
              {err && <div className="text-sm text-rose-600" data-testid="reset-error">{err}</div>}
              <Button type="submit" disabled={loading || !token} className="w-full bg-[#0A2540] hover:bg-[#061A2E]" data-testid="reset-submit-btn">
                {loading ? "Updating…" : "Set new password"}
              </Button>
              <div className="text-sm text-center text-slate-600">
                Back to <Link to="/login" className="text-[#1D4ED8] font-medium">sign in</Link>
              </div>
            </form>
          </>
        )}
      </Card>
    </div>
  );
}
